import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { api } from 'src/services';

type GetCurrentPractitionerResponse = {
  aura_status_filters: unknown[];
  cello_token: string;
  entitlements: {
    features: Record<string, { isEntitled: boolean }>;
    permissions: Record<string, unknown>;
  };
  has_appointments: boolean;
  intercom: {
    user_hash: string;
  };
  intercom_hash: string;
  is_self_signup: boolean | null;
  onboarding_complete: boolean;
  onboarding_demo: boolean;
  org_id: number;
  org_name: string;
  org_settings: {
    Intake_ambient_combo: boolean;
    aura_archival_enabled: boolean;
    ehr_patient_chart_url: string;
    ehr_precharting_enabled: boolean;
    email_expiry_hours: number;
    email_major_theme_color: string;
    enable_ambient_ai: boolean;
    enable_ambient_in_visit_enrich_sections: boolean;
    enable_ambient_in_visit_enrich_v2: boolean;
    enable_ambient_in_visit_styling: boolean;
    enable_bulk_appointment_upload: boolean;
    enable_chat_classification: boolean;
    enable_chat_followup: boolean;
    enable_chat_moderation: boolean;
    enable_dot_phrases_in_dictation: boolean;
    enable_icd10_codes_simple: boolean;
    enable_in_visit_summary_correction: boolean;
    enable_intake_summary_feedback: boolean;
    enable_intake_summary_regeneration: boolean;
    enable_language_selection: boolean;
    enable_live_transcription: boolean;
    enable_parallel_summary_enrichment: boolean;
    enable_patient_email_notifications: boolean;
    enable_patient_sms_notifications: boolean;
    enable_practitioner_email_notifications: boolean;
    enable_service_request_upload: boolean;
    enable_template_exchange: boolean;
    hipaa_authentication: boolean;
    intake_summary_regeneration_passes: number;
    show_appointment_time: boolean;
    show_qr_code: boolean;
    vca_archival_enabled: boolean;
  };
  org_uuid: string;
  organization_end_time: string;
  organization_start_time: string;
  practitioner_ehr_id: number | null;
  practitioner_first_name: string;
  practitioner_id: number;
  practitioner_last_name: string;
  practitioner_name: string;
  primary_specialty_id: number;
  referral_code: string | null;
  roles: string[];
  stripe_customer_id: string;
  stripe_subscription_id: string | null;
};

export const useGetCurrentPractitioner = () => {
  const settingsOrgValue = useSelector<any, GetCurrentPractitionerResponse>(
    (state) => state?.appointmentState.settingsOrgValue
  );

  const data = useQuery({
    queryKey: ['currentPractitioner'],
    queryFn: () =>
      settingsOrgValue
        ? Promise.resolve(settingsOrgValue)
        : api
            .get<GetCurrentPractitionerResponse>('/self')
            .then((res) => res.data),
  });

  useEffect(() => {
    if (settingsOrgValue) {
      data.refetch();
    }
  }, [settingsOrgValue]);

  const isAdmin = data.data?.roles?.some((role) =>
    role?.toLowerCase().includes('admin')
  );
  const isSuperAdmin = data.data?.roles?.some((role) =>
    ['superadmin'].includes(role)
  );

  return {
    ...data,
    isAdmin: !!isAdmin,
    isSuperAdmin: !!isSuperAdmin,
  };
};
